import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import styled, { createGlobalStyle } from 'styled-components';
import { node, bool, func } from 'prop-types';
import Div100vh from 'react-div-100vh';

const MODAL_ROOT = document.getElementById('modal');

/**
 * Component for a highly customizable modal.
 *
 * @component
 * @example
 * return (
 *   <Modal isOpen={true}>
 *    <ModalContainer>this is awesome</ModalContainer>
 *   </Modal>
 * )
 */

const Modal = ({
  children,
  isOpen: isOpenProp,
  withGlobal = true,
  onClose,
}) => {
  const node = useRef();
  const [isOpen, setIsOpen] = useState(isOpenProp);

  const handleClickOutside = (e) => {
    if (node.current !== e.target) {
      // inside click
      return;
    }
    // outside click
    setIsOpen(false);
  };

  useEffect(() => {
    if (!isOpen) {
      onClose();
    }
  }, [isOpen]);

  useEffect(() => {
    setIsOpen(isOpenProp);
  }, [isOpenProp]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  if (isOpen) {
    return createPortal(
      <Background
        id="container"
        onClick={(e) => {
          if (
            e.currentTarget.id === 'container' &&
            e.target.id === 'container'
          ) {
            setIsOpen(false);
          }
        }}
        ref={node}
      >
        {withGlobal && <GlobalStyle />}
        {children}
      </Background>,
      MODAL_ROOT,
    );
  }
  return null;
};

const GlobalStyle = createGlobalStyle`
  html {
    /* stylelint-disable-next-line */
    font-size: 62.5% !important;
    box-sizing: border-box;
    -webkit-font-smoothing: antialiased;
    touch-action: manipulation;
  }
`;

const Background = styled(Div100vh)`
  z-index: 2000;
  position: fixed;
  width: 100vw;
  height: 100vh;
  top: 0;
  left: 0;
  background: rgba(0, 0, 0, 0.3);
`;

Modal.defaultProps = {
  isOpen: false,
  // eslint-disable-next-line no-empty-function
  onClose: () => {},
};

Modal.propTypes = {
  children: node.isRequired,
  onClose: func,
  isOpen: bool,
  withGlobal: bool,
};

export default Modal;
